import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS, NAVBAR_CTA } from '../data';
import './Navbar.css';

// ── Çamlıca Çalıştay Logosu ──────────────────────────────
// SVG logo: terazi (adalet) + çınar yaprağı birleşimi
function LogoMark({ light }) {
  const fill = light ? 'rgba(255,255,255,0.95)' : 'white';
  const accent = '#e8752a';
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Arka plan */}
      <rect width="40" height="40" rx="8" fill="url(#logo-gradient)" />
      {/* Terazi sütunu */}
      <rect x="19" y="8" width="2" height="22" fill={fill} rx="1"/>
      {/* Terazi kolu */}
      <rect x="10" y="14" width="20" height="2" fill={fill} rx="1"/>
      {/* Sol kefe */}
      <circle cx="11" cy="16" r="1.5" fill={fill}/>
      <path d="M8 22 Q11 19 14 22" stroke={fill} strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      {/* Sağ kefe */}
      <circle cx="29" cy="16" r="1.5" fill={fill}/>
      <path d="M26 20 Q29 17 32 20" stroke={fill} strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      {/* Alt taban */}
      <rect x="13" y="29" width="14" height="2" fill={fill} rx="1"/>
      {/* Vurgu nokta */}
      <circle cx="20" cy="10" r="2" fill={accent}/>
      <defs>
        <linearGradient id="logo-gradient" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#c0392b"/>
          <stop offset="100%" stopColor="#d35400"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  return (
    <motion.nav
      className={`navbar ${scrolled ? 'scrolled' : ''} ${isTransparent ? 'transparent' : ''}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="navbar-container">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <LogoMark light={isTransparent} />
          <span className="logo-text">
            <span className="logo-title">Çamlıca</span>
            <span className="logo-sub">Çalıştay</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="navbar-links">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="navbar-actions">
          <Link to={NAVBAR_CTA.path} className="nav-cta">
            {NAVBAR_CTA.label}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
          <button className="mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menü">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            {NAV_LINKS.map((link, i) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={link.path}
                  className={`mobile-link ${location.pathname === link.path ? 'active' : ''}`}
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
            <Link to={NAVBAR_CTA.path} className="mobile-cta">{NAVBAR_CTA.label} →</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
